import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, Search, X } from "lucide-react";


/**
 * Nationality picker used by the Counsellor add-student flow.
 *
 * Not a shadcn <Select>: we need search-as-you-type across 130 rows,
 * and shadcn's Select is scroll-heavy for lists this long. We build a
 * lightweight combobox: a search input + filtered list + click-away
 * close. Value is the ISO alpha-2 code; label shows the flag + name.
 */
export function NationalitySelect({ value, onChange, testId = "nationality-select" }) {
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/counsellors/countries")
      .then((r) => setCountries(r.data.results || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selected = useMemo(
    () => countries.find((c) => c.code === (value || "").toUpperCase()),
    [countries, value],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return countries;
    const t = q.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(t) || c.code.toLowerCase().startsWith(t),
    );
  }, [countries, q]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between border border-stone-300 rounded-md px-3 py-2 text-sm bg-white hover:border-stone-500"
      >
        <span className="truncate">
          {selected ? (
            <>
              <span className="mr-2">{selected.flag}</span>
              {selected.name}
              <span className="text-stone-400 font-mono text-[10px] ml-2">
                {selected.code}
              </span>
            </>
          ) : (
            <span className="text-stone-400">Select nationality…</span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-stone-400" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 border-b border-stone-100 px-3 py-2">
            <Search className="w-3.5 h-3.5 text-stone-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 130 countries…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid={`${testId}-search`}
            />
            {q && (
              <X
                className="w-3.5 h-3.5 text-stone-400 cursor-pointer"
                onClick={() => setQ("")}
              />
            )}
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-stone-500 italic">
                No match. If your country is missing, contact us.
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                    setQ("");
                  }}
                  data-testid={`${testId}-option-${c.code}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-stone-50 ${
                    value === c.code ? "bg-stone-100" : ""
                  }`}
                >
                  <span>{c.flag}</span>
                  <span className="flex-1">{c.name}</span>
                  <span className="font-mono text-[10px] text-stone-400">{c.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
